import React from 'react';
import loadable from '@loadable/component';
import { MutableSnapshot, RecoilRoot } from 'recoil';
import { Loading } from '~/common/components/ui-elements';
import { IReviewsProps } from '~/common/models/interfaces/reviews';
import { Breadcrumb, CallToAction, SubNavigationItem } from '~/common/models';
import {
  CompareRangeModelState,
  CompareOtherModelState,
  CompareOtherModels,
  CompareRangeModel,
  HeaderBanner,
  HeaderBannerStore,
  SecondaryNavState,
  BreadcrumbState,
  CompareRangePopupModel,
  CompareRangePopupModelState,
  IntentSlideUpBoxModelState,
  IntentSlideUpBoxModel,
  PopupBlocker,
  PopupBlockerState,
} from './store';
import {
  ModelColorPickerState,
  ModelColourPicker,
} from '~/macros/model-colour-picker/store';

const ModelLandingPage = loadable(
  () => import('./components/model-landing-page'),
  {
    resolveComponent: (component) => component.ModelLandingPage,
    fallback: <Loading height="100vh" />,
  }
);

interface RootComponentProps {
  context: any;
  location: string;
  url: string;
  tempalteAlias: string;
  headerBanner: HeaderBanner;
  compareRangeModel: CompareRangeModel;
  compareOtherModels: CompareOtherModels;
  compareRangePopupModels: CompareRangePopupModel[];
  subNavigations: SubNavigationItem[];
  secondaryNavTitle: string;
  reviews: IReviewsProps;
  disableModelWalk: boolean;
  breadcrumbs: Breadcrumb[];
  modelColourPicker: ModelColourPicker;
  intentSlideUpBoxModel: IntentSlideUpBoxModel;
  butterMenu?: {
    ctaBuildYourOwn?: CallToAction;
    ctaBookTestDrive?: CallToAction;
    ctaDownloadBrochure?: CallToAction;
    ctaFindDealer?: CallToAction;
    ctaBookService?: CallToAction;
    ctaShowroom?: CallToAction;
  };
  popupBlocker: PopupBlocker;
}

export const RootComponent = (props: RootComponentProps) => {
  const initialize = ({ set }: MutableSnapshot) => {
    // DEBUG LOG: RecoilRoot Initialization
    console.log('[FlowTrace] RecoilRoot initializing with props:', props);

    const uniqueMarketingCategories = [
      ...new Set(
        props.compareRangeModel.modelVariants?.map((variant) =>
          (variant.marketingCategory || '').trim()
        )
      ),
    ].sort((a, b) => a.localeCompare(b));
    // Sort model variants based on marketing category order.
    // If no marketing category order is provided, sort by unique marketing categories per model variant
    const sortedModelVariants = [
      ...(props.compareRangeModel.modelVariants || []),
    ].sort((a, b) => {
      const order = props.compareRangeModel.marketingCategoryOrder?.length
        ? props.compareRangeModel.marketingCategoryOrder
        : uniqueMarketingCategories;

      const indexA = order.indexOf(a.marketingCategory || '');
      const indexB = order.indexOf(b.marketingCategory || '');

      const getSortIndex = (index) =>
        index === -1 ? Number.MAX_SAFE_INTEGER : index;

      return getSortIndex(indexA) - getSortIndex(indexB);
    });

    set(CompareRangeModelState, {
      ...props.compareRangeModel,
      modelVariants: sortedModelVariants,
    });
    set(CompareOtherModelState, props.compareOtherModels);
    set(IntentSlideUpBoxModelState, props.intentSlideUpBoxModel);
    set(
      CompareRangePopupModelState,
      props.compareRangePopupModels.map((m) => ({
        ...m,
        _active: false,
        variants: m.variants?.map((v) => ({
          ...v,
          _order: Number.MAX_SAFE_INTEGER,
          _selected: false,
        })),
      }))
    );
    set(BreadcrumbState, props.breadcrumbs);
    set(SecondaryNavState, {
      secondaryNavTitle: props.secondaryNavTitle,
      items: props.subNavigations,
    });

    set(HeaderBannerStore, {
      url: props.url,
      headerBanner: props.headerBanner,
      reviews: props.reviews,
    });

    set(ModelColorPickerState, props.modelColourPicker);
    set(PopupBlockerState, props.popupBlocker);
  };

  return (
    <RecoilRoot initializeState={initialize}>
      <ModelLandingPage
        tempalteAlias={props.tempalteAlias}
        modelName={props.compareRangeModel.modelName}
        butterMenu={props.butterMenu}
        disableModelWalk={props.disableModelWalk}
      />
    </RecoilRoot>
  );
};
